import { ReactElement, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Card,
  Container,
  Typography,
  Alert,
  Skeleton,
  IconButton,
} from '@mui/material'
import { ArrowBack } from '@mui/icons-material'
import { useAppSelector } from 'src/store/hooks'
import {
  fetchAssignments,
  selectAssignments,
  selectAssignmentsStatus,
} from 'src/v3/profile/profile.slice'
import {
  fetchCourses,
  fetchSections,
  selectCourses,
  selectCoursesStatus,
  selectSections,
  selectSectionsStatus,
} from 'src/v3/self-paced/self-paced.slice'
import {
  fetchLinkedServices,
  selectLinkedServices,
  selectLinkedServicesStatus,
} from 'src/v3/manage/manage.slice'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import AssignmentDetailCard from './components/assignment-detail-card'
import AssignmentCourseCard from './components/assignment-course-card'
import { AssignmentDetail, AssignmentCourseDetail } from './types'

export default function AssignmentDetailPage(): ReactElement {
  let { assignmentId } = useParams<{ assignmentId: string }>()
  let navigate = useNavigate()

  let assignments = useAppSelector(selectAssignments)
  let assignmentsStatus = useAppSelector(selectAssignmentsStatus)
  let courses = useAppSelector(selectCourses)
  let coursesStatus = useAppSelector(selectCoursesStatus)
  let sections = useAppSelector(selectSections)
  let sectionsStatus = useAppSelector(selectSectionsStatus)
  let linkedServices = useAppSelector(selectLinkedServices)
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)

  useAutoFetch(assignmentsStatus, fetchAssignments)
  useAutoFetch(coursesStatus, fetchCourses)
  useAutoFetch(sectionsStatus, fetchSections)
  useAutoFetch(linkedServicesStatus, fetchLinkedServices)

  // Find the assignment by ID
  let assignment = useMemo(
    () => assignments.find((a) => a.id === assignmentId),
    [assignments, assignmentId],
  )

  // Enrich assignment with course details (CE hours, section names)
  let enrichedAssignment: AssignmentDetail | null = useMemo(() => {
    if (!assignment) return null

    let enrichedCourses: AssignmentCourseDetail[] = assignment.courses.map(
      (assignmentCourse) => {
        let fullCourse = courses.find((c) => c.id === assignmentCourse.id)
        let section = fullCourse
          ? sections.find((s) => s.id === fullCourse.sectionID)
          : undefined

        return {
          id: assignmentCourse.id,
          name: assignmentCourse.name,
          status: assignmentCourse.status,
          finalDate: assignmentCourse.finalDate,
          imageName: fullCourse?.imageName,
          sectionName: section?.name,
          ceHours: fullCourse?.length || 0,
        }
      },
    )

    return {
      id: assignment.id,
      name: assignment.name,
      status: assignment.status,
      dueDate: assignment.dueDate,
      dateCompleted: assignment.dateCompleted,
      dateCreated: '',
      linkedServiceID: assignment.linkedServiceID,
      courses: enrichedCourses,
    }
  }, [assignment, courses, sections])

  let linkedServiceName = useMemo(
    () =>
      enrichedAssignment
        ? linkedServices.find(
            (ls) => ls.id === enrichedAssignment.linkedServiceID,
          )?.name
        : undefined,
    [enrichedAssignment, linkedServices],
  )

  let isLoaded =
    assignmentsStatus === 'fulfilled' &&
    coursesStatus === 'fulfilled' &&
    sectionsStatus === 'fulfilled'

  let handleBackClick = () => {
    navigate('/assignments')
  }

  // Loading state
  if (!isLoaded) {
    return (
      <Container maxWidth="xl" className="py-6">
        <Box
          role="status"
          aria-live="polite"
          aria-busy="true"
          aria-label="Loading assignment details"
        >
          {/* Back button and title skeleton */}
          <Box className="flex items-center gap-2 mb-4">
            <Skeleton
              variant="circular"
              sx={{
                width: 44,
                height: 44,
              }}
            />
            <Skeleton
              variant="text"
              sx={{
                width: 300,
                height: 48,
              }}
            />
          </Box>

          {/* Assignment detail card skeleton */}
          <Card sx={{ p: 4, mb: 3 }}>
            <Box className="grid grid-cols-1 gap-4 mb-4 md:grid-cols-2">
              <Box>
                <Skeleton
                  variant="text"
                  sx={{
                    width: 100,
                    height: 20,
                    mb: 1,
                  }}
                />
                <Skeleton
                  variant="text"
                  sx={{
                    width: 140,
                    height: 24,
                  }}
                />
              </Box>
              <Box>
                <Skeleton
                  variant="text"
                  sx={{
                    width: 80,
                    height: 20,
                    mb: 1,
                  }}
                />
                <Box className="flex items-center gap-2">
                  <Skeleton
                    variant="circular"
                    sx={{
                      width: 20,
                      height: 20,
                    }}
                  />
                  <Skeleton
                    variant="text"
                    sx={{
                      width: 140,
                      height: 24,
                    }}
                  />
                </Box>
                <Skeleton
                  variant="text"
                  sx={{
                    width: 120,
                    height: 20,
                    mt: 0.5,
                  }}
                />
              </Box>
              <Box>
                <Skeleton
                  variant="text"
                  sx={{
                    width: 80,
                    height: 20,
                    mb: 1,
                  }}
                />
                <Skeleton
                  variant="text"
                  sx={{
                    width: 100,
                    height: 24,
                  }}
                />
              </Box>
            </Box>
            <Box
              sx={{ p: 3, bgcolor: 'background.paperDark', borderRadius: 2 }}
            >
              <Box className="flex items-center justify-between mb-2">
                <Skeleton
                  variant="text"
                  sx={{
                    width: 80,
                    height: 24,
                  }}
                />
                <Skeleton
                  variant="text"
                  sx={{
                    width: 60,
                    height: 32,
                  }}
                />
              </Box>
              <Skeleton
                variant="rectangular"
                sx={{
                  width: '100%',
                  height: 12,
                  borderRadius: 6,
                  mb: 1,
                }}
              />
              <Skeleton
                variant="text"
                sx={{
                  width: 100,
                  height: 20,
                  mx: 'auto',
                }}
              />
            </Box>
          </Card>

          {/* Course list heading skeleton */}
          <Skeleton
            variant="text"
            sx={{
              width: 200,
              height: 36,
              mb: 3,
            }}
          />

          {/* Course card skeletons */}
          <Box className="grid grid-cols-1 gap-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <Card key={index} sx={{ p: 3 }}>
                <Box className="flex items-center gap-3">
                  <Skeleton
                    variant="circular"
                    sx={{
                      width: 40,
                      height: 40,
                    }}
                  />
                  <Skeleton
                    variant="rectangular"
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: 2,
                    }}
                  />
                  <Box className="flex-1">
                    <Skeleton
                      variant="text"
                      sx={{
                        width: '60%',
                        height: 28,
                        mb: 1,
                      }}
                    />
                    <Box className="flex flex-wrap items-center gap-2">
                      <Skeleton
                        variant="rectangular"
                        sx={{
                          width: 80,
                          height: 24,
                          borderRadius: 12,
                        }}
                      />
                      <Skeleton
                        variant="rectangular"
                        sx={{
                          width: 100,
                          height: 24,
                          borderRadius: 12,
                        }}
                      />
                    </Box>
                  </Box>
                  <Skeleton
                    variant="rectangular"
                    sx={{
                      width: 140,
                      height: 36,
                      borderRadius: 1,
                    }}
                  />
                </Box>
              </Card>
            ))}
          </Box>
          <span className="sr-only">Loading assignment details...</span>
        </Box>
      </Container>
    )
  }

  // Assignment not found
  if (isLoaded && !enrichedAssignment) {
    return (
      <Container maxWidth="xl" className="py-6">
        <Card
          sx={{ p: 6, textAlign: 'center' }}
          role="status"
          aria-live="polite"
        >
          <Alert severity="error" sx={{ mb: 3 }} role="alert">
            <Typography variant="h6" sx={{ mb: 1 }}>
              Assignment Not Found
            </Typography>
            <Typography variant="body2">
              The assignment you're looking for doesn't exist or may have been
              deleted.
            </Typography>
          </Alert>
          <IconButton
            onClick={handleBackClick}
            aria-label="Go back to assignments"
            sx={{ minWidth: 44, minHeight: 44 }}
          >
            <ArrowBack />
          </IconButton>
        </Card>
      </Container>
    )
  }

  // Assignment has no courses
  if (enrichedAssignment && enrichedAssignment.courses.length === 0) {
    return (
      <Container maxWidth="xl" className="py-6">
        <Box className="flex items-center gap-2 mb-4">
          <IconButton
            onClick={handleBackClick}
            aria-label="Go back to assignments"
            sx={{ minWidth: 44, minHeight: 44 }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h4" component="h1">
            {enrichedAssignment.name}
          </Typography>
        </Box>
        <AssignmentDetailCard
          assignment={enrichedAssignment}
          linkedServiceName={linkedServiceName}
        />
        <Card
          sx={{ p: 6, textAlign: 'center', mt: 3 }}
          role="status"
          aria-live="polite"
        >
          <Typography
            color="text.secondary"
            variant="h6"
            sx={{
              mb: 1,
            }}
          >
            No Courses in Assignment
          </Typography>
          <Typography color="text.secondary" variant="body2">
            This assignment doesn't contain any courses yet.
          </Typography>
        </Card>
      </Container>
    )
  }

  // Safety check - should not happen but prevents runtime errors
  if (!enrichedAssignment) {
    return (
      <Container maxWidth="xl" className="py-6">
        <Card
          sx={{ p: 6, textAlign: 'center' }}
          role="status"
          aria-live="polite"
        >
          <Alert severity="error" sx={{ mb: 3 }} role="alert">
            <Typography variant="h6" sx={{ mb: 1 }}>
              Assignment Not Found
            </Typography>
            <Typography variant="body2">
              Unable to load assignment details. Please try again.
            </Typography>
          </Alert>
          <IconButton
            onClick={handleBackClick}
            aria-label="Go back to assignments"
            sx={{ minWidth: 44, minHeight: 44 }}
          >
            <ArrowBack />
          </IconButton>
        </Card>
      </Container>
    )
  }

  // Success - display assignment detail
  return (
    <Container maxWidth="xl" className="py-6">
      <Box className="flex items-center gap-2 mb-4">
        <IconButton
          onClick={handleBackClick}
          aria-label="Go back to assignments"
          sx={{ minWidth: 44, minHeight: 44 }}
        >
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" component="h1">
          {enrichedAssignment.name}
        </Typography>
      </Box>
      <AssignmentDetailCard
        assignment={enrichedAssignment}
        linkedServiceName={linkedServiceName}
      />
      {/* Course List */}
      <Box
        className="mt-6"
        component="section"
        aria-labelledby="courses-heading"
      >
        <Typography
          id="courses-heading"
          variant="h5"
          sx={{ mb: 3, fontWeight: 600 }}
        >
          Courses ({enrichedAssignment.courses.length})
        </Typography>

        <Box
          className="grid grid-cols-1 gap-3"
          role="list"
          aria-labelledby="courses-heading"
        >
          {enrichedAssignment.courses.map((course) => (
            <Box key={course.id} role="listitem">
              <AssignmentCourseCard course={course} />
            </Box>
          ))}
        </Box>
      </Box>
    </Container>
  )
}
